import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ModalShell } from "@/components/ui/modal-shell";
import { useCustomerAuth, type CustomerCodeChallenge, type CustomerProfile } from "@/hooks/use-customer-auth";
import { CODIGO_CHEGA_EM_30S, ELE_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";

const REENVIO_S = 30;

// Passo do código no WhatsApp (loja com pontos): o login de quem já é cliente e o "confirmar para ver os pontos"
// usam o mesmo campo. Mesmo cooldown de reenvio do servidor (30 s).
export function CustomerCodeStep({
  maskedNumber,
  submitting,
  error,
  onSubmit,
  onResend,
  submitLabel = "Entrar",
}: {
  maskedNumber: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  submitLabel?: string;
}) {
  const [code, setCode] = useState("");
  const [espera, setEspera] = useState(REENVIO_S);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const enviar = () => {
    if (code.length === 4 && !submitting) onSubmit(code);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Mandamos um código para o WhatsApp <span className="font-semibold text-foreground">{maskedNumber}</span>. {ELE_CHEGA_EM_30S}
      </p>
      <Input
        value={code}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "").slice(0, 4);
          setCode(v);
          if (v.length === 4 && !submitting) onSubmit(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") enviar();
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="0000"
        className="h-14 text-center text-2xl font-bold tracking-[0.5em]"
        autoFocus
        aria-label="Código recebido no WhatsApp"
        data-testid="customer-code-input"
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button className="w-full" onClick={enviar} disabled={code.length < 4 || submitting} data-testid="customer-code-submit">
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
      <button
        type="button"
        className="w-full text-sm text-primary font-medium disabled:text-muted-foreground"
        disabled={espera > 0 || submitting}
        onClick={() => {
          setCode("");
          setEspera(REENVIO_S);
          onResend();
        }}
      >
        {espera > 0 ? `Reenviar código em 0:${String(espera).padStart(2, "0")}` : "Reenviar código"}
      </button>
    </div>
  );
}

/** Já logado sem ter confirmado o número: manda o código e confirma (para ver e trocar pontos). */
export function ConfirmNumberDialog({
  open,
  onOpenChange,
  storeSlug,
  onVerified,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeSlug: string;
  onVerified?: (customer: CustomerProfile) => void;
}) {
  const { requestCode, verifyCode } = useCustomerAuth();
  const [challenge, setChallenge] = useState<CustomerCodeChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [carregando, setCarregando] = useState(false);

  const pedir = async () => {
    setCarregando(true);
    setError(null);
    const r = await requestCode(storeSlug);
    setCarregando(false);
    if (r.alreadyVerified) {
      onOpenChange(false);
      return;
    }
    if (r.error) setError(r.error);
    if (r.challenge) setChallenge(r.challenge);
  };

  useEffect(() => {
    if (open) {
      setChallenge(null);
      setError(null);
      void pedir();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ModalShell open={open} onOpenChange={onOpenChange} title="Confirme que é você" size="sm">
      {challenge ? (
        <CustomerCodeStep
          maskedNumber={challenge.maskedNumber}
          submitting={submitting}
          error={error}
          submitLabel="Confirmar"
          onResend={() => void pedir()}
          onSubmit={async (code) => {
            setSubmitting(true);
            setError(null);
            const r = await verifyCode(challenge.requestId, code);
            setSubmitting(false);
            if (r.error) {
              setError(r.error);
              return;
            }
            if (r.customer) {
              onVerified?.(r.customer);
              onOpenChange(false);
            }
          }}
        />
      ) : (
        <div className="py-6 flex flex-col items-center gap-3 text-sm text-muted-foreground">
          {carregando ? <Loader2 className="w-6 h-6 animate-spin" /> : null}
          {error ? <p className="text-destructive text-center">{error}</p> : carregando ? "Enviando o código…" : null}
          {carregando && !error && <p className="text-center" data-testid="aviso-codigo-30s">{CODIGO_CHEGA_EM_30S}</p>}
          {error && (
            <Button variant="outline" onClick={() => void pedir()}>
              Tentar de novo
            </Button>
          )}
        </div>
      )}
    </ModalShell>
  );
}
